const keyOf = (value) => String(value || "").trim().toLowerCase();

export const EXCEL_CELL_TEXT_LIMIT = 32767;

export const limitExcelCellText = (value) => {
  if (typeof value !== "string" || value.length <= EXCEL_CELL_TEXT_LIMIT) return value;
  return `${value.slice(0, EXCEL_CELL_TEXT_LIMIT - 1)}…`;
};

const numberOf = (value) => {
  const parsed = Number(String(value ?? 0).replace(/[,£%]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const timestampOf = (value) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value || NaN).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

export const databaseAdviserName = (adviser = {}, fallback = "") => {
  const firstName = String(
    adviser.preferredName || adviser.firstName || adviser.forename || "",
  ).trim();
  const lastName = String(adviser.lastName || adviser.surname || "").trim();
  const structuredName = [firstName, lastName].filter(Boolean).join(" ");
  const storedName = String(adviser.name || adviser.adviser || "").trim();

  if (
    firstName &&
    !lastName &&
    storedName.toLowerCase().startsWith(`${firstName.toLowerCase()} `)
  ) {
    return storedName;
  }

  return String(structuredName || storedName || fallback).trim();
};

const adviserDirectory = (advisers = []) => new Map(
  advisers
    .map((adviser) => [keyOf(adviser.email || adviser.id), adviser])
    .filter(([email]) => email),
);

const resolveAdviserRow = ({
  sourceRow,
  directory,
  emailKeys,
  nameKey,
  nameWithEmail = false,
}) => {
  const row = { ...sourceRow };
  const email = keyOf(firstValue(row, emailKeys));
  const adviser = directory.get(email);
  if (!adviser) return row;
  const resolvedName = databaseAdviserName(adviser, email);
  row[nameKey] = nameWithEmail
    ? `${resolvedName} (${adviser.email || adviser.id || email})`
    : resolvedName;
  const outputEmailKey = emailKeys.find((key) => !key.startsWith("__"));
  if (outputEmailKey) row[outputEmailKey] = adviser.email || adviser.id || email;
  return row;
};

const firstValue = (row, keys) => keys
  .map((key) => row[key])
  .find((value) => String(value || "").trim()) || "";

export const consolidateGlobalSummaryRows = ({
  rows = [],
  advisers = [],
  emailKeys = ["Adviser Email"],
  entryKeys = [],
  nameKey = "Adviser",
  nameWithEmail = false,
  sumColumns = [],
  dateColumns = [],
}) => {
  const directory = adviserDirectory(advisers);
  const groups = new Map();

  rows.forEach((sourceRow, sourceIndex) => {
    const email = keyOf(firstValue(sourceRow, emailKeys));
    const entry = keyOf(firstValue(sourceRow, entryKeys));
    const groupKey = email
      ? `email:${email}`
      : entry
        ? `entry:${entry}`
        : `row:${sourceIndex}`;
    const current = groups.get(groupKey);
    if (!current) {
      groups.set(groupKey, { row: { ...sourceRow }, email });
      return;
    }

    sumColumns.forEach((column) => {
      current.row[column] = numberOf(current.row[column]) + numberOf(sourceRow[column]);
    });
    dateColumns.forEach((column) => {
      const existingTime = timestampOf(current.row[column]);
      const candidateTime = timestampOf(sourceRow[column]);
      if (candidateTime !== null && (existingTime === null || candidateTime > existingTime)) {
        current.row[column] = sourceRow[column];
      }
    });

    Object.keys(sourceRow).forEach((column) => {
      if (
        column.startsWith("__") ||
        emailKeys.includes(column) ||
        sumColumns.includes(column) ||
        dateColumns.includes(column) ||
        column === nameKey
      ) return;
      const existing = String(current.row[column] ?? "").trim();
      const candidate = String(sourceRow[column] ?? "").trim();
      if (!candidate || candidate === existing) return;
      if (!existing) {
        current.row[column] = sourceRow[column];
        return;
      }
      if (/URL$/i.test(column)) {
        current.row[column] = "";
        return;
      }
      const values = new Set(existing.split("; ").filter(Boolean));
      values.add(candidate);
      current.row[column] = [...values].join("; ");
    });
  });

  return [...groups.values()].map(({ row }) => resolveAdviserRow({
    sourceRow: row,
    directory,
    emailKeys,
    nameKey,
    nameWithEmail,
  }));
};

export const globalChargeSheetRows = ({
  category,
  rows = [],
  advisers = [],
}) =>
  [
    "AML Checks",
    "Miscellaneous",
    "Paraplanning",
    "ProCeding",
    "Technology",
    "Website & Graphics",
    "Additional Payments",
    "Advances",
  ].includes(category)
    ? rows.map((row) => resolveAdviserRow({
        sourceRow: row,
        directory: adviserDirectory(advisers),
        emailKeys: ["Adviser Email"],
        nameKey: "Adviser",
      }))
    : consolidateGlobalSummaryRows({
        rows,
        advisers,
        emailKeys: ["Adviser Email"],
        entryKeys: ["Adviser"],
        sumColumns: ["Amount Charged", "Expected Amount", "Variance"],
        dateColumns: ["Date"],
      });
